import { useEffect, useState, useCallback } from 'react'

const ctrlBtn = {
  width: '40px', height: '40px', borderRadius: '50%',
  background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)',
  color: 'white', cursor: 'pointer', display: 'flex',
  alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0,
}

const navBtn = {
  ...ctrlBtn,
  width: '44px', height: '44px',
  position: 'absolute', top: '50%', transform: 'translateY(-50%)',
}

// Full-screen image viewer. `images` = array of src strings, `index` = current
// position, `onNavigate(newIndex)` moves, `onClose()` dismisses.
export default function Lightbox({ images, index, onClose, onNavigate }) {
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)

  // Reset zoom/rotation whenever the image changes
  useEffect(() => { setZoom(1); setRotation(0) }, [index])

  const prev = useCallback(
    () => onNavigate((index - 1 + images.length) % images.length),
    [index, images.length, onNavigate]
  )
  const next = useCallback(
    () => onNavigate((index + 1) % images.length),
    [index, images.length, onNavigate]
  )

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose, prev, next])

  if (index == null || !images.length) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <img
        src={images[index]}
        alt={`Enlarged view ${index + 1} of ${images.length}`}
        style={{
          maxWidth: '88vw', maxHeight: '76vh', objectFit: 'contain',
          transform: `scale(${zoom}) rotate(${rotation}deg)`,
          transition: 'transform 0.2s ease',
          borderRadius: 'var(--radius)',
        }}
      />

      {/* Close */}
      <button onClick={onClose} aria-label="Close" style={{ ...ctrlBtn, position: 'absolute', top: '20px', right: '20px' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" />
        </svg>
      </button>

      {/* Prev / Next */}
      {images.length > 1 && (
        <>
          <button onClick={prev} aria-label="Previous image" style={{ ...navBtn, left: '16px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button onClick={next} aria-label="Next image" style={{ ...navBtn, right: '16px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </>
      )}

      {/* Counter */}
      {images.length > 1 && (
        <div style={{ position: 'absolute', top: '26px', left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.75)', fontSize: '13px' }}>
          {index + 1} / {images.length}
        </div>
      )}

      {/* Bottom controls: zoom + rotate */}
      <div style={{
        position: 'absolute', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
        display: 'flex', gap: '10px',
      }}>
        <button onClick={() => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))} aria-label="Zoom in" style={ctrlBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><line x1="16" y1="16" x2="21" y2="21" />
            <line x1="8" y1="11" x2="14" y2="11" /><line x1="11" y1="8" x2="11" y2="14" />
          </svg>
        </button>
        <button onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))} aria-label="Zoom out" style={ctrlBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="7" /><line x1="16" y1="16" x2="21" y2="21" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>
        <button onClick={() => { setZoom(1); setRotation(0) }} aria-label="Reset view" style={{ ...ctrlBtn, fontSize: '11px', fontWeight: 600 }}>
          1:1
        </button>
        <button onClick={() => setRotation(r => r - 90)} aria-label="Rotate left" style={ctrlBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
          </svg>
        </button>
        <button onClick={() => setRotation(r => r + 90)} aria-label="Rotate right" style={ctrlBtn}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10" />
          </svg>
        </button>
      </div>
    </div>
  )
}
